import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../components/controls/FormInput";
import Autocomplete from "../../components/Places/PlacesAutocomplete";
import ProgressStep from "../EventCreation/Components/ProgressStepper";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../../components/controls/Button";

export default function ShippingManagementUserInterface() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("warehouses");
  const [warehouses, setWarehouses] = useState([]);
  const [carrierAccounts, setCarrierAccounts] = useState([]);
  const [isCarrierWizardOpen, setIsCarrierWizardOpen] = useState(false);
  const [carrierWizardStep, setCarrierWizardStep] = useState(0);
  const [carrierWizardMode, setCarrierWizardMode] = useState("create");
  const [carrierWizardAutoOpened, setCarrierWizardAutoOpened] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [testOk, setTestOk] = useState(null);

  const { currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  // ---- draft entrepôt (ajout/édition) ----
  const [draftWarehouse, setDraftWarehouse] = useState({
    id: null, // important pour PUT
    identifier: "",
    name: "",
    addressFull: "",
    pickupEnabled: false,
    pickupInstructions: "",
    userId: currentUserId,
    location: null, // { Latitude, Longitude, FormattedAddress } (provenant de l'autocomplete)
  });

  const DEFAULT_DEST = {
    country: "CA",
    stateOrProvince: "QC",
    city: "Montréal",
    postalOrZip: "H2X1Y4", // sans espace c'est OK aussi
  };

  // ---- draft transporteur (ajout/édition) ----
  const initialCarrierState = {
    id: null,
    identifier: "",
    providerName: "",
    accountNickname: "",
    connectionStatus: "disconnected",
    connectionMethod: "", // "platform" | "username_password"
    connectionMode: "manual", // radios
    environment: "sandbox", // select
    apiCredentials: {
      apiKey: "",
      apiSecret: "",
      username: "",
      password: "",
      contractId: "",
      customerNumber: "",
    },
  };
  const [draftCarrierAccount, setDraftCarrierAccount] =
    useState(initialCarrierState);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Warehouses
  useEffect(() => {
    if (!currentUserId) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/warehouses/my?userId=${currentUserId}`
        );
        if (!res.ok) throw new Error("GET warehouses/my failed");
        const data = await res.json();
        setWarehouses(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setWarehouses([]);
      }
    })();
  }, [API_BASE, currentUserId]);

  const handleSaveWarehouse = async () => {
    if (!draftWarehouse.name || !draftWarehouse.addressFull) return;

    const loc = draftWarehouse.location || {};
    const dto = {
      userId: currentUserId,
      name: draftWarehouse.name,
      addressFull: draftWarehouse.addressFull,
      pickupEnabled: !!draftWarehouse.pickupEnabled,
      pickupInstructions: draftWarehouse.pickupInstructions || "",
      location: {
        latitude:
          loc.Latitude ?? loc.latitude ?? loc.lat ?? loc.coords?.lat ?? 0,
        longitude:
          loc.Longitude ?? loc.longitude ?? loc.lng ?? loc.coords?.lng ?? 0,
        formattedAddress:
          loc.FormattedAddress ??
          loc.formattedAddress ??
          draftWarehouse.addressFull,
        regionId: null,
      },
    };

    try {
      let saved;
      if (draftWarehouse.id) {
        const res = await fetch(
          `${API_BASE}/api/warehouses/${draftWarehouse.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
          }
        );
        if (!res.ok) throw new Error("PUT warehouse failed");
        saved = await res.json();
        setWarehouses((list) =>
          list.map((w) => (w.id === saved.id ? saved : w))
        );
      } else {
        const res = await fetch(`${API_BASE}/api/warehouses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dto),
        });
        if (!res.ok) throw new Error("POST warehouse failed");
        saved = await res.json();
        setWarehouses((list) => [saved, ...list]);
      }
      resetDraftWarehouse();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteWarehouse = async (id) => {
    if (!id) return;
    try {
      const res = await fetch(`${API_BASE}/api/warehouses/${id}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204) throw new Error("DELETE failed");
      setWarehouses((list) => list.filter((w) => w.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  // =========================
  // Carrier Accounts (API)
  // =========================

  async function fetchCarrierAccounts() {
    if (!currentUserId) return [];

    const res = await fetch(
      `${API_BASE}/api/CarrierAccounts/my?userId=${currentUserId}`
    );
    if (!res.ok) throw new Error("GET carriers/my failed");
    return res.json();
  }
  async function createCarrierAccount(dto) {
    const res = await fetch(`${API_BASE}/api/CarrierAccounts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    });
    if (!res.ok) throw new Error("POST carrier failed");
    return res.json();
  }
  async function updateCarrierAccount(id, dto) {
    const res = await fetch(`${API_BASE}/api/CarrierAccounts/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    });
    if (!res.ok) throw new Error("PUT carrier failed");
    return res.json();
  }
  async function deleteCarrierAccountApi(id) {
    const res = await fetch(`${API_BASE}/api/CarrierAccounts/${id}`, {
      method: "DELETE",
    });
    if (!res.ok && res.status !== 204) throw new Error("DELETE carrier failed");
  }
  async function testCarrierConnectionApi(dto) {
    const res = await fetch(`${API_BASE}/api/CarrierAccounts/test-connection`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    });
    if (!res.ok) throw new Error("TEST carrier failed");
    return res.json(); // { ok: boolean, message?: string }
  }

  function mapProviderName(uiValue) {
    // ton UI envoie "canada_post"; le back tolère souvent "canada post"/"canadapost"
    if (!uiValue) return "";
    const s = uiValue.trim().toLowerCase();
    if (
      s === "canada_post" ||
      s === "canada post" ||
      s === "canadapost" ||
      s === "postes canada"
    )
      return "canada post";
    return s;
  }

  function pruneEmpty(obj) {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }

  // charger carriers au montage
  useEffect(() => {
    if (!currentUserId) return;

    (async () => {
      try {
        const data = await fetchCarrierAccounts();
        setCarrierAccounts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setCarrierAccounts([]);
      }
    })();
  }, [API_BASE, currentUserId]);

  // SAVE carrier (POST/PUT)
  async function handleSaveCarrierAccount() {
    if (
      !draftCarrierAccount.providerName ||
      !draftCarrierAccount.accountNickname
    )
      return;

    const upsert = {
      providerName: mapProviderName(draftCarrierAccount.providerName),
      accountNickname: draftCarrierAccount.accountNickname,
      connectionMethod: draftCarrierAccount.connectionMethod, // "platform" | "username_password"
      connectionMode: draftCarrierAccount.connectionMode, // "platform" | "manual"
      environment: draftCarrierAccount.environment, // "sandbox" | "production"
      apiCredentials: { ...draftCarrierAccount.apiCredentials },
      userId: currentUserId,
    };

    try {
      let saved;
      if (draftCarrierAccount.id) {
        saved = await updateCarrierAccount(draftCarrierAccount.id, upsert);
        setCarrierAccounts((list) =>
          list.map((c) => (c.id === saved.id ? saved : c))
        );
      } else {
        saved = await createCarrierAccount(upsert);
        setCarrierAccounts((list) => [saved, ...list]);
      }
      setIsCarrierWizardOpen(false);
      resetDraftCarrierAccount();
      setCarrierWizardStep(0);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleDeleteCarrierAccount(id) {
    if (!id) return;
    try {
      await deleteCarrierAccountApi(id);
      setCarrierAccounts((list) => list.filter((c) => c.id !== id));
    } catch (e) {
      console.error(e);
    }
  }

  async function testConnection() {
    const payload = {
      providerName: mapProviderName(draftCarrierAccount.providerName),
      accountNickname: (draftCarrierAccount.accountNickname || "").trim(),
      connectionMethod: draftCarrierAccount.connectionMethod,
      connectionMode: draftCarrierAccount.connectionMode,
      environment: draftCarrierAccount.environment,
      apiCredentials: pruneEmpty(draftCarrierAccount.apiCredentials),
    };

    try {
      setTesting(true);
      setTestMsg("");
      setTestOk(null);

      const r = await testCarrierConnectionApi(payload); // { ok, message, statusCode }
      setDraftCarrierAccount((cur) => ({
        ...cur,
        connectionStatus: r.ok ? "connected" : "disconnected",
      }));
      setTestOk(r.ok);
      setTestMsg(r.message || (r.ok ? "Connexion valide." : "Échec du test."));
    } catch (e) {
      console.error(e);
      setTestOk(false);
      setTestMsg("Erreur lors du test de connexion.");
    } finally {
      setTesting(false);
    }
  }

  // =========================================================
  // Logique UI locale
  // =========================================================

  const providerCatalog = [
    {
      value: "canada_post",
      labelKey: "shipping.providers.canada_post",
      methods: [
        {
          value: "platform",
          labelKey: "shipping.connectionMethods.canadaPostPlatform",
          fields: [],
        },
        {
          value: "username_password",
          labelKey: "shipping.connectionMethods.username_password",
          fields: ["username", "password", "customerNumber", "contractId"],
        },
      ],
    },
    /*
    {
      value: "purolator",
      labelKey: "shipping.providers.purolator",
      methods: [
        {
          value: "api_key",
          labelKey: "shipping.connectionMethods.api_key",
          fields: ["apiKey", "apiSecret"],
        },
      ],
    },
    {
      value: "ups",
      labelKey: "shipping.providers.ups",
      methods: [
        {
          value: "api_key",
          labelKey: "shipping.connectionMethods.api_key",
          fields: ["apiKey", "apiSecret", "username", "password"],
        },
      ],
    },
    {
      value: "flagship",
      labelKey: "shipping.providers.flagship",
      methods: [
        {
          value: "api_key",
          labelKey: "shipping.connectionMethods.api_key",
          fields: ["apiKey", "apiSecret"],
        },
      ],
    },*/
  ];

  useEffect(() => {
    if (
      activeSection === "carrierAccounts" &&
      carrierAccounts.length === 0 &&
      !carrierWizardAutoOpened
    ) {
      setCarrierWizardAutoOpened(true);
      setIsCarrierWizardOpen(true);
      setCarrierWizardStep(0);
      setCarrierWizardMode("create");
    }
  }, [activeSection, carrierAccounts.length, carrierWizardAutoOpened]);

  function resetDraftWarehouse() {
    setDraftWarehouse({
      id: null,
      identifier: "",
      name: "",
      addressFull: "",
      pickupEnabled: false,
      pickupInstructions: "",
      location: null,
    });
  }

  function resetDraftCarrierAccount() {
    setDraftCarrierAccount({
      ...initialCarrierState,
      id: null,
      apiCredentials: {
        apiKey: "",
        apiSecret: "",
        username: "",
        password: "",
        contractId: "",
        customerNumber: "",
      },
    });
  }

  function setProvider(providerValue) {
    const selectedProvider =
      providerCatalog.find((p) => p.value === providerValue) || null;

    // par défaut: si le provider supporte "platform", on choisit platform; sinon 1er method
    const hasPlatform = selectedProvider?.methods.some(
      (m) => m.value === "platform"
    );
    const defaultMode = "manual";
    const defaultMethod = "username_password";

    setDraftCarrierAccount((current) => ({
      ...current,
      providerName: providerValue,
      connectionMode: defaultMode,
      connectionMethod: defaultMethod,
      environment: current.environment || "sandbox",
      connectionStatus: "disconnected",
      apiCredentials: {
        apiKey: "",
        apiSecret: "",
        username: "",
        password: "",
        contractId: "",
        customerNumber: "",
      },
    }));
  }

  function setConnectionModeSynced(mode) {
    // mappe le mode UI vers la bonne connectionMethod attendue par visibleFields()
    // platform -> "platform"
    // manual   -> "username_password" (pour Canada Post dans ton catalog)
    const method = mode === "platform" ? "platform" : "username_password";

    setDraftCarrierAccount((c) => ({
      ...c,
      connectionMode: mode,
      connectionMethod: method,
      connectionStatus: "disconnected",
      apiCredentials: {
        apiKey: "",
        apiSecret: "",
        username: "",
        password: "",
        contractId: "",
        customerNumber: "",
      },
    }));
  }

  const normalizeTo = (to) => {
    const country = (to?.country || "CA").toUpperCase();
    const postal = to?.postalOrZip
      ? country === "CA"
        ? to.postalOrZip.toUpperCase().replace(/[ -]/g, "")
        : to.postalOrZip
      : "";
    return {
      country,
      stateOrProvince: to?.stateOrProvince || "",
      city: to?.city || "",
      postalOrZip: postal,
    };
  };

  function visibleFields() {
    const selectedProvider = providerCatalog.find(
      (provider) => provider.value === draftCarrierAccount.providerName
    );
    if (!selectedProvider) return [];
    const selectedMethod = selectedProvider.methods.find(
      (method) => method.value === draftCarrierAccount.connectionMethod
    );
    if (!selectedMethod) return [];
    return selectedMethod.fields;
  }

  function editWarehouse(warehouse) {
    // normaliser la location pour le draft
    const loc = warehouse.location || {};
    setDraftWarehouse({
      id: warehouse.id ?? null,
      identifier: warehouse.identifier ?? "",
      name: warehouse.name ?? "",
      userId: currentUserId,
      addressFull: warehouse.addressFull ?? "",
      pickupEnabled: !!warehouse.pickupEnabled,
      pickupInstructions: warehouse.pickupInstructions || "",
      location: {
        Latitude: loc.latitude ?? loc.Latitude ?? 0,
        Longitude: loc.longitude ?? loc.Longitude ?? 0,
        FormattedAddress:
          loc.formattedAddress ??
          loc.FormattedAddress ??
          warehouse.addressFull ??
          "",
      },
    });
    setActiveSection("warehouses");
  }

  function openCarrierWizardForCreate() {
    setCarrierWizardMode("create");
    resetDraftCarrierAccount();
    setCarrierWizardStep(0);
    setIsCarrierWizardOpen(true);
  }

  function openCarrierWizardForEdit(c) {
    const providerUi = providerApiToUi(c.providerName);
    const modeFromApi = (c.connectionMode || "").toLowerCase(); // si ton DTO le renvoie
    const methodFromApi = (c.connectionMethod || "").toLowerCase();

    // Fallback robustes
    const inferredMode = modeFromApi
      ? modeFromApi
      : methodFromApi === "username_password"
      ? "manual"
      : "platform";

    const inferredMethod =
      methodFromApi ||
      (inferredMode === "manual" ? "username_password" : "platform");

    setCarrierWizardMode("edit");
    setDraftCarrierAccount({
      id: c.id ?? null,
      identifier: c.identifier ?? "",
      providerName: providerUi, // <- valeur présente dans ton <select>
      accountNickname: c.accountNickname ?? "",
      connectionStatus: c.connectionStatus ?? "disconnected",
      connectionMethod: inferredMethod, // <- visibleFields() en dépend
      connectionMode: inferredMode, // <- coches radio
      environment: (c.environment || "sandbox").toLowerCase(),
      apiCredentials: {
        apiKey: "",
        apiSecret: "",
        username: "",
        password: "",
        contractId: "",
        customerNumber: "",
      },
    });
    setCarrierWizardStep(0);
    setIsCarrierWizardOpen(true);
  }

  function deleteCarrierAccountLocal(identifier) {
    // gardé si jamais tu veux encore supprimer côté local-only
    setCarrierAccounts((list) =>
      list.filter((carrier) => carrier.identifier !== identifier)
    );
  }

  function updateCredential(field, value) {
    setDraftCarrierAccount((current) => ({
      ...current,
      apiCredentials: { ...current.apiCredentials, [field]: value },
    }));
  }

  const stepLabels = [
    t("shipping.steps.provider"),
    t("shipping.steps.method"),
    t("shipping.steps.credentials"),
    t("shipping.steps.finalize"),
  ];
  const canGoNextFromStep0 = Boolean(
    draftCarrierAccount.providerName &&
      (draftCarrierAccount.accountNickname || "").trim()
  );
  const canGoNextFromStep1 = Boolean(
    draftCarrierAccount.connectionMethod || draftCarrierAccount.connectionMode
  );
  const isLastStep = carrierWizardStep === 3;

  function providerApiToUi(v) {
    const s = (v || "").trim().toLowerCase();
    if (["canada post", "canadapost", "postes canada"].includes(s))
      return "canada_post";
    return v || "";
  }

  return (
    <div className="app-container p-8 space-y-8 text-primary">
      <header className="header-container space-y-4">
        <h1 className="header-title text-2xl font-semibold">
          {t("shipping.title")}
        </h1>
        <div role="tablist" className="flex gap-3">
          <Button
            as="button"
            type="button"
            color={activeSection === "warehouses" ? "mint" : "primary"}
            size="md"
            className="px-4 py-2"
            onClick={() => setActiveSection("warehouses")}
          >
            {t("shipping.sections.warehouses")}
          </Button>

          <Button
            as="button"
            type="button"
            color={activeSection === "carrierAccounts" ? "mint" : "primary"}
            size="md"
            className="px-4 py-2"
            onClick={() => setActiveSection("carrierAccounts")}
          >
            {t("shipping.sections.carrierAccounts")}
          </Button>
        </div>
      </header>

      {activeSection === "warehouses" && (
        <section className="section-container space-y-6">
          <div className="card-container p-6 rounded-2xl border bg-white shadow-sm space-y-5">
            <h2 className="card-title text-lg font-semibold">
              {t("shipping.warehouses.createOrEdit")}
            </h2>
            <FormInput
              label={t("shipping.fields.warehouseName")}
              name="name"
              value={draftWarehouse.name}
              onChange={(name, value) =>
                setDraftWarehouse((current) => ({
                  ...current,
                  name: value,
                }))
              }
              placeholder={t("shipping.placeholders.warehouseName")}
              showRequiredOrOptional={false}
            />
            <div className="space-y-2">
              <label className="form-label text-sm font-medium">
                {t("shipping.fields.warehouseAddress")}
              </label>
              <Autocomplete
                value={draftWarehouse.addressFull}
                onSelect={(newValue) =>
                  setDraftWarehouse((current) => ({
                    ...current,
                    addressFull: newValue.formattedAddress,
                    location: newValue, // brut; normalisé lors du POST/PUT
                  }))
                }
                placeholder={t("shipping.placeholders.warehouseAddress")}
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={draftWarehouse.pickupEnabled}
                onChange={(e) =>
                  setDraftWarehouse((current) => ({
                    ...current,
                    pickupEnabled: e.target.checked,
                  }))
                }
              />
              <span>{t("shipping.fields.enablePickup")}</span>
            </div>
            {draftWarehouse.pickupEnabled && (
              <FormInput
                label={t("shipping.fields.pickupInstructions")}
                name="pickupInstructions"
                value={draftWarehouse.pickupInstructions}
                onChange={(name, value) =>
                  setDraftWarehouse((current) => ({
                    ...current,
                    pickupInstructions: value,
                  }))
                }
                placeholder={t("shipping.placeholders.pickupInstructions")}
                showRequiredOrOptional={false}
              />
            )}
            <div className="flex gap-3">
              <Button
                type="button"
                color="primary"
                size="md"
                onClick={handleSaveWarehouse}
              >
                {t("shipping.actions.saveWarehouse")}
              </Button>
              <Button
                type="button"
                color="primary"
                size="md"
                onClick={resetDraftWarehouse}
              >
                {t("shipping.actions.reset")}
              </Button>
            </div>
          </div>

          <div className="card-list p-6 rounded-2xl border bg-white shadow-sm space-y-4">
            <div className="pb-3 mb-1 border-b font-semibold">
              {t("shipping.warehouses.existing")}
            </div>
            <ul className="rounded-xl border bg-white divide-y">
              {warehouses.length === 0 && (
                <li className="p-4 text-sm text-gray-500">
                  {t("shipping.empty.states.noWarehouses")}
                </li>
              )}
              {warehouses.map((warehouse) => (
                <li
                  key={warehouse.id || warehouse.identifier}
                  className="p-4 flex items-start justify-between gap-4"
                >
                  <div>
                    <div className="font-medium">{warehouse.name}</div>
                    <div className="text-sm text-gray-600">
                      {warehouse.addressFull}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={() => editWarehouse(warehouse)}
                    >
                      {t("shipping.actions.edit")}
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={() =>
                        handleDeleteWarehouse(warehouse.id || null)
                      }
                      disabled={!warehouse.id}
                      title={!warehouse.id ? "Non synchronisé" : ""}
                    >
                      {t("shipping.actions.delete")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {activeSection === "carrierAccounts" && (
        <section className="section-container space-y-6">
          <div className="rounded-2xl   flex justify-end">
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={openCarrierWizardForCreate}
            >
              {t("shipping.actions.saveCarrier")}
            </Button>
          </div>

          <div className="card-list p-6 rounded-2xl border bg-white shadow-sm space-y-4">
            <div className="pb-3 mb-1 border-b font-semibold">
              {t("shipping.carriers.existing")}
            </div>
            <ul className="rounded-xl border bg-white divide-y">
              {carrierAccounts.length === 0 && (
                <li className="p-4 text-sm text-gray-500">
                  {t("shipping.empty.states.noCarriers")}
                </li>
              )}
              {carrierAccounts.map((c) => (
                <li
                  key={c.id || c.identifier}
                  className="p-4 flex items-start justify-between gap-4"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{c.providerName}</div>
                    <div className="text-sm text-gray-600">
                      {c.accountNickname}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs rounded-full px-2 py-0.5 bg-gray-100 border">
                      Mode:{" "}
                      {c.connectionMode === "manual"
                        ? "Saisie manuelle"
                        : "Plateforme"}
                    </span>
                    <span className="text-xs rounded-full px-2 py-0.5 bg-gray-100 border">
                      Env:{" "}
                      {c.environment === "production"
                        ? "Production"
                        : "Sandbox"}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={() => openCarrierWizardForEdit(c)}
                    >
                      {t("shipping.actions.edit")}
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={() => handleDeleteCarrierAccount(c.id)}
                      disabled={!c.id}
                      title={!c.id ? "Non synchronisé" : ""}
                    >
                      {t("shipping.actions.delete")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {isCarrierWizardOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsCarrierWizardOpen(false)}
          />
          <div className="relative w-full max-w-3xl bg-white rounded-2xl p-6 overflow-visible grid grid-rows-[auto,1fr,auto] gap-6">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-semibold">
                {carrierWizardMode === "create"
                  ? t("shipping.carriers.createOrEdit")
                  : t("shipping.actions.edit")}
              </h3>
              <Button
                type="button"
                color="primary"
                size="sm"
                className="rounded-full p-1"
                onClick={() => setIsCarrierWizardOpen(false)}
              >
                <XMarkIcon className="h-5 w-5 rounded-full" />
              </Button>
            </div>

            <div className="space-y-6 overflow-y-auto overflow-x-visible px-1">
              <ProgressStep
                currentStep={carrierWizardStep + 1}
                steps={stepLabels}
              />

              {carrierWizardStep === 0 && (
                <div className="space-y-6">
                  <div className="space-y-2 relative overflow-visible">
                    <label className="text-sm font-medium">
                      {t("shipping.fields.providerPreset")}
                    </label>
                    <select
                      className="rounded-lg border p-3 bg-white shadow-sm focus:outline-none  w-full"
                      value={draftCarrierAccount.providerName}
                      onChange={(e) => setProvider(e.target.value)}
                    >
                      <option value="">
                        {t("shipping.placeholders.selectProvider")}
                      </option>
                      {providerCatalog.map((provider) => (
                        <option key={provider.value} value={provider.value}>
                          {t(provider.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:w-1/2">
                    <FormInput
                      label={t("shipping.fields.carrierAccountNickname")}
                      name="accountNickname"
                      value={draftCarrierAccount.accountNickname}
                      onChange={(name, value) =>
                        setDraftCarrierAccount((c) => ({
                          ...c,
                          accountNickname: value,
                        }))
                      }
                      placeholder={t(
                        "shipping.placeholders.carrierAccountNickname"
                      )}
                      required={true}
                    />
                  </div>
                </div>
              )}

              {carrierWizardStep === 1 && (
                <div className="space-y-6">
                  {/* nouveau: mode de connexion */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Mode de connexion
                    </label>
                    <div className="flex items-center gap-4">
                      {/*} <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={
                            draftCarrierAccount.connectionMode === "platform"
                          }
                          onChange={() => setConnectionModeSynced("platform")}
                        />
                        <span>Connexion directe (portail Poste Canada)</span>
                      </label>*/}
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={
                            true
                            // draftCarrierAccount.connectionMode === "manual"
                          }
                          onChange={() => setConnectionModeSynced("manual")}
                        />
                        <span>Saisie manuelle</span>
                      </label>
                    </div>
                  </div>

                  {/* optionnel: environnement */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Environnement</label>
                    <select
                      className="rounded-lg border p-3 bg-white shadow-sm focus:outline-none w-full"
                      value={draftCarrierAccount.environment || "sandbox"}
                      onChange={(e) =>
                        setDraftCarrierAccount((c) => ({
                          ...c,
                          environment: e.target.value,
                        }))
                      }
                    >
                      <option value="production">Production</option>
                      <option value="sandbox">Sandbox (TEST)</option>
                    </select>
                  </div>
                </div>
              )}

              {carrierWizardStep === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleFields().includes("apiKey") && (
                    <FormInput
                      label={t("shipping.fields.apiKey")}
                      name="apiKey"
                      value={draftCarrierAccount.apiCredentials.apiKey}
                      onChange={(name, value) =>
                        updateCredential("apiKey", value)
                      }
                      placeholder={t("shipping.placeholders.apiKey")}
                      showRequiredOrOptional={false}
                    />
                  )}

                  {visibleFields().includes("apiSecret") && (
                    <FormInput
                      type="password"
                      label={t("shipping.fields.apiSecret")}
                      name="apiSecret"
                      value={draftCarrierAccount.apiCredentials.apiSecret}
                      onChange={(name, value) =>
                        updateCredential("apiSecret", value)
                      }
                      placeholder={t("shipping.placeholders.apiSecret")}
                      showRequiredOrOptional={false}
                    />
                  )}

                  {visibleFields().includes("username") && (
                    <FormInput
                      label={t("shipping.fields.username")}
                      name="username"
                      value={draftCarrierAccount.apiCredentials.username}
                      onChange={(name, value) =>
                        updateCredential("username", value)
                      }
                      placeholder={t("shipping.placeholders.username")}
                      showRequiredOrOptional={false}
                    />
                  )}

                  {visibleFields().includes("password") && (
                    <FormInput
                      type="password"
                      label={t("shipping.fields.password")}
                      name="password"
                      value={draftCarrierAccount.apiCredentials.password}
                      onChange={(name, value) =>
                        updateCredential("password", value)
                      }
                      placeholder={t("shipping.placeholders.password")}
                      showRequiredOrOptional={false}
                    />
                  )}

                  {visibleFields().includes("contractId") && (
                    <FormInput
                      label={t("shipping.fields.contractId")}
                      name="contractId"
                      value={draftCarrierAccount.apiCredentials.contractId}
                      onChange={(name, value) =>
                        updateCredential("contractId", value)
                      }
                      placeholder={t("shipping.placeholders.contractId")}
                      showRequiredOrOptional={false}
                    />
                  )}

                  {visibleFields().includes("customerNumber") && (
                    <FormInput
                      label={t("global.customerNumber")}
                      name="customerNumber"
                      value={draftCarrierAccount.apiCredentials.customerNumber}
                      onChange={(name, value) =>
                        updateCredential("customerNumber", value)
                      }
                      placeholder="1234567890"
                      showRequiredOrOptional={false}
                    />
                  )}
                </div>
              )}

              {carrierWizardStep === 3 && (
                <div className="space-y-3">
                  <label className="text-sm font-medium">
                    {t("shipping.fields.connectionStatus")}
                  </label>

                  <div className="flex items-center gap-3">
                    {draftCarrierAccount.connectionStatus === "connected"
                      ? t("shipping.connection.connected")
                      : t("shipping.connection.disconnected")}

                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={testConnection}
                      disabled={testing}
                    >
                      {testing
                        ? t("shipping.actions.testing")
                        : t("shipping.actions.testConnection")}
                    </Button>
                  </div>

                  {testOk !== null && (
                    <div
                      className={`rounded-lg border p-3 text-sm ${
                        testOk
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-red-300 bg-red-50 text-red-700"
                      }`}
                    >
                      {testMsg}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                color="primary"
                size="md"
                onClick={() => setCarrierWizardStep((s) => Math.max(0, s - 1))}
                disabled={carrierWizardStep === 0}
              >
                {t("shipping.actions.back")}
              </Button>
              <div className="flex items-center gap-3">
                {!isLastStep && (
                  <Button
                    type="button"
                    color="primary"
                    size="md"
                    onClick={() =>
                      setCarrierWizardStep((s) => Math.min(3, s + 1))
                    }
                    disabled={
                      (carrierWizardStep === 0 && !canGoNextFromStep0) ||
                      (carrierWizardStep === 1 && !canGoNextFromStep1)
                    }
                  >
                    {t("shipping.actions.next")}
                  </Button>
                )}
                {isLastStep && (
                  <Button
                    type="button"
                    color="primary"
                    size="md"
                    onClick={handleSaveCarrierAccount}
                    disabled={
                      !(draftCarrierAccount.accountNickname || "").trim()
                    }
                  >
                    {t("shipping.actions.saveCarrier")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
